import { useMemo } from 'react';
import { useBookingLinkOptions, type BookingLinkOption } from '../../api/trigger-options.api';

/* ── Meeting-booked trigger settings (JT1-1920) ──
 *
 * Which JobTetris booking links start the campaign (any user's; none ticked = any link) and who
 * it enrols. Used by the campaign builder's Enrollment step and the workflow builder.
 */

export type MeetingPersonType = 'candidate' | 'contact' | 'any';

export const PERSON_TYPE_OPTIONS: { value: MeetingPersonType; label: string }[] = [
  { value: 'any', label: 'Either: candidates and contacts' },
  { value: 'candidate', label: 'Candidates only' },
  { value: 'contact', label: 'Contacts only' },
];

/** The links grouped by the person who owns them, keeping saved links JobTetris no longer has. */
export function groupBookingLinks(options: BookingLinkOption[], selected: string[]) {
  const groups = new Map<string, { value: string; label: string }[]>();
  for (const o of options) {
    const list = groups.get(o.owner_name) ?? [];
    list.push({ value: o.value, label: o.label });
    groups.set(o.owner_name, list);
  }
  const missing = selected.filter((id) => !options.some((o) => o.value === id));
  if (missing.length) {
    groups.set('No longer in JobTetris', missing.map((id) => ({ value: id, label: 'A booking link that was removed' })));
  }
  return [...groups.entries()].map(([owner, links]) => ({ owner, links }));
}

export function MeetingBookedFields({
  eventTypeIds,
  personType,
  onChange,
  disabled,
}: {
  eventTypeIds: string[];
  personType: MeetingPersonType;
  onChange: (next: { event_type_ids: string[]; person_type: MeetingPersonType }) => void;
  disabled?: boolean;
}) {
  const { data, isLoading } = useBookingLinkOptions();
  const groups = useMemo(() => groupBookingLinks(data?.data ?? [], eventTypeIds), [data, eventTypeIds]);

  const toggle = (id: string) => {
    const next = eventTypeIds.includes(id) ? eventTypeIds.filter((x) => x !== id) : [...eventTypeIds, id];
    onChange({ event_type_ids: next, person_type: personType });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <span style={labelStyle()}>Booking links</span>
        {isLoading ? (
          <p style={hintStyle()}>Loading booking links…</p>
        ) : groups.length === 0 ? (
          <p style={hintStyle()} data-testid="mb-no-links">
            No JobTetris booking links yet. They appear here within about 30 minutes of being set up in JobTetris
            (Account, Scheduling). Until then, any booking starts the campaign.
          </p>
        ) : (
          <>
            {groups.map((g) => (
              <fieldset key={g.owner} style={{ border: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                <legend style={{ ...hintStyle(), fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-1)' }}>{g.owner}</legend>
                {g.links.map((l) => (
                  <label key={l.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)', cursor: disabled ? 'not-allowed' : 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={eventTypeIds.includes(l.value)}
                      disabled={disabled}
                      onChange={() => toggle(l.value)}
                    />
                    {l.label}
                  </label>
                ))}
              </fieldset>
            ))}
            <p style={hintStyle()}>
              {eventTypeIds.length === 0
                ? 'None ticked: a booking on any of these links starts the campaign.'
                : 'Only bookings on the ticked links start the campaign.'}
            </p>
          </>
        )}
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        <span style={labelStyle()}>Who to enrol</span>
        <select
          value={personType}
          disabled={disabled}
          onChange={(e) => onChange({ event_type_ids: eventTypeIds, person_type: e.target.value as MeetingPersonType })}
          style={{
            height: 'var(--btn-height)',
            padding: '0 var(--space-3)',
            borderRadius: 'var(--radius-input)',
            border: '1px solid var(--input-border, var(--color-border-default))',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-sm)',
            backgroundColor: 'var(--color-surface-white)',
          }}
        >
          {PERSON_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span style={hintStyle()}>
          Engage matches the person who booked by their email. A booking by someone of the other kind is ignored.
        </span>
      </label>
    </div>
  );
}

function labelStyle(): React.CSSProperties {
  return {
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-secondary)',
  };
}

function hintStyle(): React.CSSProperties {
  return { margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 'var(--leading-normal)' };
}
